/**
 * ENG-5641 — rolling connectivity-probe orchestration (manager CLI).
 *
 * Given an agent's installed integrations, selects the ones DUE for a probe
 * (last_connectivity_check_at older than the interval, NULLS first), bounds the
 * batch so we don't probe everything at once (the rolling cadence: wake every
 * 5 min, probe each integration ~hourly), runs each probe via the executor, and
 * returns the report batch to POST to /host/integration-connectivity.
 *
 * Pure + injectable (executor, clock, interval, batch cap) so due-selection +
 * batching + report-mapping are unit-testable. The real probe capabilities
 * (fetch / runCli / mcpProbe) and the POST live in the poll-loop wiring.
 */

import {
  executeConnectivityProbe as defaultExecuteProbe,
  type ConnectivityProbeDeps,
  type ConnectivityProbeTarget,
} from './connectivity-probe-executor.js';
import type {
  ConnectivityCause,
  ConnectivityEvidence,
  ConnectivityProbeOutcome,
  ConnectivityTestOverride,
  ToolkitSourceType,
} from '@augmented/core/integrations';
import type { IntegrationAuthType } from '@augmented/core';

/** One installed integration as returned (enriched) by /host/agent-integrations. */
export interface ProbeRunnerIntegration {
  id: string;
  definition_id: string;
  scope: 'agent' | 'team' | 'organization';
  auth_type?: IntegrationAuthType | null;
  source_type?: ToolkitSourceType | null;
  credentials?: Record<string, unknown>;
  last_connectivity_check_at?: string | null;
  /** CLI binary name (when this is a cli_tool integration). */
  cli_binary?: string;
  /** MCP server key in the agent's .mcp.json (when this is an mcp_server integration). */
  mcp_server_key?: string;
  /**
   * ENG-6242 — the toolkit's `connectivity_test` override (managed/Composio),
   * enriched by `/host/agent-integrations`. Threaded to the executor so the
   * managed tool-call leg calls the prescribed tool, matching the Test path.
   */
  connectivity_test?: ConnectivityTestOverride | null;
  /**
   * ENG-8049 - inline `.mcp.json`-shaped server entry for a quarantined
   * integration (removed from `.mcp.json`), reconstructed by the manager via the
   * framework adapter so its connectivity can still be probed for auto-resume.
   * Threaded to the executor's MCP legs; absent for normal (active) rows.
   */
  inline_server_entry?: unknown;
  /**
   * ENG-7077 — the catalog `integration_definitions.remote_mcp` spec, already
   * enriched onto this payload by `/host/agent-integrations` for provisioning.
   * Threaded to the executor so the resolver can recognise an ADR-0033
   * static-token hosted remote MCP (monday, peec, anchor-browser); without it
   * they resolve to `builtin` and never make a network call.
   */
  remote_mcp?: { url?: string | null } | null;
  /**
   * ENG-8363 — the row's creation time, forwarded by `/host/agent-integrations`
   * (added for ENG-7575's first-connect backoff). Threaded to the executor so an
   * MCP leg can tell a `.mcp.json` key that has not been WRITTEN yet from one
   * that never will be: the manager probes before it writes the file in the same
   * poll, so the first probe of a newly-added integration always finds its key
   * missing.
   */
  created_at?: string | null;
  /**
   * ENG-9718 — the reconnect settling deadline, forwarded by
   * `/host/agent-integrations`. A reconnect returns the row to the forwarded set
   * and the host writes `.mcp.json` one poll later; a probe in that gap must not
   * grade the still-absent key a hard `down`.
   */
  reconnect_quiesced_until?: string | null;
  /**
   * ENG-8363 — this row is ENG-7916-quarantined and therefore deliberately
   * absent from `.mcp.json` (ENG-8036 probes it only to observe recovery), so a
   * missing key must never escalate to `down`.
   */
  quarantined?: boolean;
}

export interface ConnectivityReport {
  integration_id: string;
  scope: 'agent' | 'team' | 'organization';
  status: ConnectivityProbeOutcome['status'];
  message?: string;
  /**
   * ENG-8226 — what the probe actually did to reach `status`. Always sent by a
   * current manager; an older manager omits it and the API defaults it to
   * `'none'`, so an unknown-provenance `ok` can never be mistaken for a
   * verified one.
   */
  evidence?: ConnectivityEvidence;
  /**
   * ENG-9173 — WHY the probe failed, as a closed vocabulary rather than prose.
   *
   * Absent from this interface until now, which is why the column ENG-9173 added
   * read `unknown` on every host-probed row: the probes classified, the API knew
   * how to persist it, and nothing carried it between them. See the send site
   * below.
   *
   * Optional on purpose in both directions — an older manager omits it, and a
   * probe that genuinely cannot classify should omit it rather than assert
   * `'unknown'`.
   */
  cause?: ConnectivityCause;
}

export interface ProbeRunnerOptions {
  now?: () => Date;
  /** A row is due when its last check is older than this (default 1h). */
  intervalMs?: number;
  /** Max probes per run — bounds the rolling batch (default 25). */
  maxPerRun?: number;
  /** Probe capabilities forwarded to the executor (fetch / runCli / mcpProbe / composioProbe). */
  probeDeps?: ConnectivityProbeDeps;
  /** Override the executor (tests). */
  executeProbe?: typeof defaultExecuteProbe;
}

export interface ProbeRunnerResult {
  /** Reports to POST up (only integrations that produced an outcome). */
  reports: ConnectivityReport[];
  /** How many integrations were due this run. */
  due: number;
  /** How many were actually probed (bounded by maxPerRun). */
  probed: number;
  /** Probed but produced no outcome (unsupported / un-wired capability). */
  skipped: number;
}

const DEFAULT_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
const DEFAULT_MAX_PER_RUN = 25;

/**
 * Is this row due for a probe on this poll?
 *
 * Three ways to be due: never probed, an unparseable timestamp, or the ordinary
 * interval has elapsed. The third clause below is ENG-9723.
 */
function isDue(
  lastCheck: string | null | undefined,
  now: number,
  intervalMs: number,
  /** ENG-9723 — the row's `reconnect_quiesced_until`, if the control plane sent one. */
  reconnectQuiescedUntil?: string | null,
): boolean {
  if (!lastCheck) return true; // never probed
  const t = Date.parse(lastCheck);
  if (Number.isNaN(t)) return true; // unparseable → treat as due
  if (now - t >= intervalMs) return true;

  // ENG-9723 — a reconnect window that has just CLOSED makes the row due now,
  // instead of waiting out the rest of the interval.
  //
  // ENG-9718 stops a probe landing mid-reconnect from branding the row `down`:
  // inside the window a reported `down` is demoted to `unverified`. That is
  // correct, but it leaves the CORRECTION up to an interval away — so right
  // after an operator clicks reconnect, which is the one moment a human is
  // actually watching, a fixed integration takes up to an hour to go green and
  // a still-broken one takes just as long to say so.
  //
  // `t < q` is the load-bearing conjunct, and without it the row is due FOREVER
  // after any reconnect. It self-limits because the API stamps
  // `last_connectivity_check_at` for ANY report that reaches it, `unverified`
  // included (`integration-connectivity-report.ts`) — so one re-probe puts
  // `t >= q` and this clause goes false. Deliberately strict `<`: at `t === q`
  // the reading was taken at the boundary and is already post-window.
  //
  // NOT added, though it reads like it belongs: "and the window closed less than
  // one interval ago". It is implied. This clause is only reached when the
  // interval clause is false, i.e. `t > now - intervalMs`; with `t < q` that
  // gives `q > now - intervalMs`. Writing it out would change no outcome and
  // would survive its own sabotage, so it is asserted as a test instead.
  //
  // Cost: a pure phase ADVANCE, not added volume — the early probe moves `t`
  // forward and the next one is a full interval later. What it does change is
  // burst shape: a bulk reconnect makes its rows due together at window close
  // rather than spread across the following hour. `maxPerRun` is the existing
  // bound on that, and it still applies.
  if (reconnectQuiescedUntil) {
    const q = Date.parse(reconnectQuiescedUntil);
    if (!Number.isNaN(q) && q <= now && t < q) return true;
  }

  return false;
}

export async function runConnectivityProbes(
  integrations: ProbeRunnerIntegration[],
  options: ProbeRunnerOptions = {},
): Promise<ProbeRunnerResult> {
  const now = (options.now?.() ?? new Date()).getTime();
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
  const maxPerRun = options.maxPerRun ?? DEFAULT_MAX_PER_RUN;
  const probeDeps = options.probeDeps ?? {};
  const execute = options.executeProbe ?? defaultExecuteProbe;

  // Due rows, oldest-first (NULLS first) so the rolling cursor clears the
  // backlog fairly and load spreads across ticks.
  const due = integrations
    .filter((i) => isDue(i.last_connectivity_check_at, now, intervalMs, i.reconnect_quiesced_until))
    .sort((a, b) => {
      const ta = a.last_connectivity_check_at ? Date.parse(a.last_connectivity_check_at) : 0;
      const tb = b.last_connectivity_check_at ? Date.parse(b.last_connectivity_check_at) : 0;
      return ta - tb;
    });

  const batch = due.slice(0, maxPerRun);
  const reports: ConnectivityReport[] = [];
  let skipped = 0;

  for (const integ of batch) {
    const target: ConnectivityProbeTarget = {
      definitionId: integ.definition_id,
      sourceType: integ.source_type ?? null,
      authType: integ.auth_type ?? null,
      credentials: integ.credentials ?? {},
      cliBinary: integ.cli_binary,
      mcpServerKey: integ.mcp_server_key,
      connectivityTest: integ.connectivity_test ?? null,
      inlineServerEntry: integ.inline_server_entry,
      remoteMcpUrl: integ.remote_mcp?.url ?? null,
      // ENG-8363: row age + quarantine status decide whether a missing
      // `.mcp.json` key is "not written yet" (transient) or "not wired" (down).
      createdAt: integ.created_at ?? null,
      // ENG-9718: the second settling anchor — see the comment above; a row
      // reconnected seconds ago is as unsettled as one created seconds ago.
      reconnectQuiescedUntil: integ.reconnect_quiesced_until ?? null,
      expectedAbsent: integ.quarantined ?? false,
    };
    let outcome: ConnectivityProbeOutcome | null;
    try {
      outcome = await execute(target, probeDeps);
    } catch (err) {
      // A probe blowing up must not fail the batch — record nothing for it.
      console.warn(`[connectivity-probe] ${integ.definition_id} threw: ${(err as Error).message}`);
      outcome = null;
    }
    if (!outcome) {
      skipped += 1;
      continue;
    }
    reports.push({
      integration_id: integ.id,
      scope: integ.scope,
      status: outcome.status,
      ...(outcome.message ? { message: outcome.message } : {}),
      // ENG-8226: report WHAT the probe did, not just what it concluded. The
      // API persists this to `last_connectivity_evidence` so an `ok` is
      // self-describing and a green verdict can be justified rather than
      // assumed. Defaults to 'none' rather than being omitted — an absent field
      // from an older manager and a probe that contacted nothing must land in
      // the same, conservative place.
      evidence: outcome.evidence ?? 'none',
      // ENG-9173's other half. That ticket added `last_connectivity_cause` and
      // the API-side write for it, and the probes compute a cause today
      // (`causeForHttp` in connectivity-http-probes.ts, and the did-not-run
      // branches in cli-probe.ts). This line is what puts it ON THE WIRE.
      //
      // Without it `ConnectivityReport` had no `cause` field at all, so the
      // host lane never sent one and the API's
      // `last_connectivity_cause: item.cause ?? 'unknown'` resolved to
      // `'unknown'` for EVERY host-probed row on the fleet, forever. The column
      // added to stop an escalation boundary matching prose was itself never
      // populated by the lane that does almost all the probing — the identical
      // parse-then-drop the ENG-9173 header describes, one layer further up.
      //
      // Omitted rather than defaulted when absent: unlike `evidence`, where a
      // missing value and "contacted nothing" must both land conservatively on
      // `'none'`, the API already coerces an absent or unrecognised cause to
      // `'unknown'` (`isConnectivityCause`, integration-connectivity-report.ts).
      // Sending an explicit `'unknown'` would be indistinguishable from an older
      // manager that cannot classify at all, and that distinction is worth
      // keeping while this rolls out across hosts.
      ...(outcome.cause ? { cause: outcome.cause } : {}),
    });
  }

  return { reports, due: due.length, probed: batch.length, skipped };
}
